// ===========================================
// Calendar Integration Section Component
// ===========================================
// Google Calendar integration for account settings page
// Available to all user roles (Admin, Teacher, Parent, Student)

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Sync as SyncIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  EventAvailable as EventIcon,
} from '@mui/icons-material';
import {
  useGoogleCalendarStatus,
  useGoogleCalendarAuthUrl,
  useDisconnectGoogleCalendar,
  useGoogleCalendars,
  useSelectCalendar,
  useUpdateCalendarSettings,
  useTriggerCalendarSync,
  useGoogleCalendarSyncErrors,
  useStudentCalendarMappings,
  useSetStudentMapping,
  useClearStudentMapping,
} from '../../../hooks/useGoogleCalendar';
import { formatSyncTime, GoogleCalendar } from '../../../api/googleCalendar.api';

// Sentinel value for the "Use default calendar" option in the per-child dropdown.
const USE_DEFAULT = '__default__';

// Calendar card colors following the brand palette
const CALENDAR_CARD_COLORS = {
  border: '#4580E4', // Blue
  bg: 'rgba(69, 128, 228, 0.08)',
};

interface Props {
  isFunMode: boolean;
}

export default function CalendarIntegrationSection({ isFunMode }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  // On disconnect, whether to delete synced lessons from Google Calendar (default) or leave them.
  const [removeEventsOnDisconnect, setRemoveEventsOnDisconnect] = useState(true);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);
  const [showConnectedAlert, setShowConnectedAlert] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // API hooks
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useGoogleCalendarStatus();
  const { data: calendars, isLoading: calendarsLoading, refetch: fetchCalendars } = useGoogleCalendars();
  const { data: syncErrors } = useGoogleCalendarSyncErrors();
  const authUrlMutation = useGoogleCalendarAuthUrl();
  const disconnectMutation = useDisconnectGoogleCalendar();
  const selectCalendarMutation = useSelectCalendar();
  const updateSettingsMutation = useUpdateCalendarSettings();
  const syncMutation = useTriggerCalendarSync();

  // Per-student mappings (parents with ≥2 children). Only queried once connected.
  const isConnected = !!status?.isConnected;
  const { data: studentMappings, isLoading: mappingsLoading } = useStudentCalendarMappings(isConnected);
  const setStudentMapping = useSetStudentMapping();
  const clearStudentMapping = useClearStudentMapping();
  const showPerChild = (studentMappings?.length ?? 0) >= 2;
  // Which child's row is mid-mutation (so we can disable just that dropdown).
  const [pendingChildId, setPendingChildId] = useState<string | null>(null);

  // Fetch the writable-calendar list when the per-child section becomes visible
  // (the dropdowns need it; the "Target Calendar" selector fetches it on demand otherwise).
  useEffect(() => {
    if (showPerChild && !calendars && !calendarsLoading) {
      fetchCalendars();
    }
  }, [showPerChild, calendars, calendarsLoading, fetchCalendars]);

  // Handle OAuth callback results from URL params
  useEffect(() => {
    const connected = searchParams.get('calendarConnected');
    const error = searchParams.get('calendarError');

    if (connected === 'true') {
      refetchStatus();
      setShowConnectedAlert(true);
      // Clear the URL params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('calendarConnected');
      setSearchParams(newParams, { replace: true });
    } else if (error) {
      setConnectionError(error);
      // Clear the URL params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('calendarError');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetchStatus]);

  // ===========================================
  // HANDLERS
  // ===========================================

  const handleConnect = async () => {
    const result = await authUrlMutation.mutateAsync();
    if (result.authUrl) {
      // Redirect to Google OAuth
      window.location.href = result.authUrl;
    }
  };

  const handleDisconnect = async () => {
    await disconnectMutation.mutateAsync(removeEventsOnDisconnect);
    setShowDisconnectDialog(false);
  };

  const handleCloseDisconnectDialog = () => {
    setShowDisconnectDialog(false);
    setRemoveEventsOnDisconnect(true); // reset to the default for next time
  };

  const handleOpenCalendarSelector = async () => {
    await fetchCalendars();
    setSelectedCalendarId(status?.calendarId || null);
    setShowCalendarDialog(true);
  };

  const handleSelectCalendar = async () => {
    const calendar = calendars?.find((c) => c.id === selectedCalendarId);
    if (calendar) {
      await selectCalendarMutation.mutateAsync({
        calendarId: calendar.id,
        calendarName: calendar.summary,
      });
      setShowCalendarDialog(false);
    }
  };

  const handleToggleSync = async () => {
    await updateSettingsMutation.mutateAsync({
      syncEnabled: !status?.syncEnabled,
    });
  };

  const handleManualSync = async (fullSync: boolean = false) => {
    await syncMutation.mutateAsync({ fullSync });
  };

  const handleChildCalendarChange = async (studentId: string, e: SelectChangeEvent) => {
    const value = e.target.value;
    setPendingChildId(studentId);
    try {
      if (value === USE_DEFAULT) {
        await clearStudentMapping.mutateAsync(studentId);
      } else {
        const calendar = calendars?.find((c) => c.id === value);
        if (calendar) {
          await setStudentMapping.mutateAsync({
            studentId,
            input: { calendarId: calendar.id, calendarName: calendar.summary },
          });
        }
      }
    } finally {
      setPendingChildId(null);
    }
  };

  // ===========================================
  // LOADING STATE
  // ===========================================

  if (statusLoading) {
    return (
      <Card
        sx={{
          mb: 3,
          ...(isFunMode && {
            borderRadius: 4,
            border: `3px solid ${CALENDAR_CARD_COLORS.border}`,
            bgcolor: CALENDAR_CARD_COLORS.bg,
            boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
          }),
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // ===========================================
  // NOT CONNECTED STATE
  // ===========================================

  if (!status?.isConnected) {
    return (
      <>
        {connectionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConnectionError(null)}>
            <AlertTitle>Connection Failed</AlertTitle>
            {connectionError === 'auth_failed'
              ? 'Failed to authorize with Google. Please try again.'
              : connectionError === 'invalid_state'
                ? 'Invalid authorization state. Please try again.'
                : 'An error occurred during authorization.'}
          </Alert>
        )}

        <Card
          sx={{
            mb: 3,
            ...(isFunMode && {
              borderRadius: 4,
              border: `3px solid ${CALENDAR_CARD_COLORS.border}`,
              bgcolor: CALENDAR_CARD_COLORS.bg,
              boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
            }),
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: isFunMode ? `${CALENDAR_CARD_COLORS.border}30` : 'primary.light',
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isFunMode ? CALENDAR_CARD_COLORS.border : 'primary.dark',
                  flexShrink: 0,
                }}
              >
                <CalendarIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Google Calendar</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Connect your Google Calendar to automatically sync your lessons.
                  Events will appear in your calendar based on your role.
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    What gets synced to your calendar:
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Lesson schedules with student/teacher details"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Real-time updates when lessons change"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleConnect}
                  disabled={authUrlMutation.isPending}
                >
                  {authUrlMutation.isPending ? 'Connecting...' : 'Connect Google Calendar'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </>
    );
  }

  // ===========================================
  // CONNECTED STATE
  // ===========================================

  return (
    <>
      {showConnectedAlert && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowConnectedAlert(false)}>
          <AlertTitle>Connected Successfully</AlertTitle>
          Your Google Calendar is now connected. Select a calendar below to start syncing.
        </Alert>
      )}

      <Card
        sx={{
          mb: 3,
          ...(isFunMode && {
            borderRadius: 4,
            border: `3px solid ${CALENDAR_CARD_COLORS.border}`,
            bgcolor: CALENDAR_CARD_COLORS.bg,
            boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
          }),
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                bgcolor: isFunMode ? `${CALENDAR_CARD_COLORS.border}30` : 'success.light',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isFunMode ? CALENDAR_CARD_COLORS.border : 'success.dark',
                flexShrink: 0,
              }}
            >
              <CalendarIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Google Calendar</Typography>
                <Chip
                  icon={<CheckIcon />}
                  label="Connected"
                  color="success"
                  size="small"
                />
              </Box>

              <Stack spacing={2} sx={{ mt: 2 }}>
                {/* Calendar Selection */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Default Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {status.calendarName || 'No calendar selected'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenCalendarSelector}
                  >
                    {status.calendarId ? 'Change' : 'Select'}
                  </Button>
                </Box>

                {/* Per-Child Calendars (parents with 2+ children) */}
                {showPerChild && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2">Per-child calendars</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Send each child's lessons to their own calendar. A child in a group
                        class gets that class in their own calendar too. Leave on "Default
                        calendar" to use the default calendar above.
                      </Typography>
                      {!status.calendarId ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Select a default calendar first to route individual children.
                        </Typography>
                      ) : mappingsLoading || calendarsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : (
                        <Stack spacing={1.5}>
                          {studentMappings?.map((child) => {
                            const childRowPending =
                              pendingChildId === child.studentId &&
                              (setStudentMapping.isPending || clearStudentMapping.isPending);
                            // If this child is mapped to a calendar that's no longer in the
                            // writable list (deleted / access revoked), keep it selectable so
                            // MUI doesn't warn about an out-of-range value and render blank.
                            const mappedMissing =
                              !child.usingDefault &&
                              !!child.calendarId &&
                              !calendars?.some((c) => c.id === child.calendarId);
                            return (
                              <Box
                                key={child.studentId}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 2,
                                }}
                              >
                                <Typography variant="body2" sx={{ flexShrink: 0 }}>
                                  {child.firstName} {child.lastName}
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <Select
                                    value={child.usingDefault ? USE_DEFAULT : child.calendarId ?? USE_DEFAULT}
                                    onChange={(e) => handleChildCalendarChange(child.studentId, e)}
                                    disabled={childRowPending || !calendars}
                                    startAdornment={
                                      childRowPending ? (
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                      ) : undefined
                                    }
                                  >
                                    <MenuItem value={USE_DEFAULT}>
                                      {status.calendarName
                                        ? `Default Calendar (${status.calendarName})`
                                        : 'Default Calendar'}
                                    </MenuItem>
                                    {calendars?.map((calendar: GoogleCalendar) => (
                                      <MenuItem key={calendar.id} value={calendar.id}>
                                        {calendar.summary}
                                        {calendar.primary ? ' (Primary)' : ''}
                                      </MenuItem>
                                    ))}
                                    {mappedMissing && (
                                      <MenuItem value={child.calendarId as string}>
                                        {child.calendarName || 'Selected calendar'} (unavailable)
                                      </MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  </>
                )}

                <Divider />

                {/* Sync Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Automatic Sync</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Keep calendar events updated automatically
                    </Typography>
                  </Box>
                  <Switch
                    checked={status.syncEnabled}
                    onChange={handleToggleSync}
                    disabled={!status.calendarId || updateSettingsMutation.isPending}
                  />
                </Box>

                {/* Sync Stats */}
                {status.syncStats && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Sync Status
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                          icon={<EventIcon />}
                          label={`${status.syncStats.synced} synced`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                        {status.syncStats.pending > 0 && (
                          <Chip
                            icon={<SyncIcon />}
                            label={`${status.syncStats.pending} pending`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {status.syncStats.errors > 0 && (
                          <Chip
                            icon={<ErrorIcon />}
                            label={`${status.syncStats.errors} errors`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Box>
                  </>
                )}

                <Divider />

                {/* Last Sync & Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">Last Sync</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatSyncTime(status.lastSyncAt)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={syncMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
                      onClick={() => handleManualSync(false)}
                      disabled={!status.calendarId || syncMutation.isPending}
                    >
                      Sync
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={() => handleManualSync(true)}
                      disabled={!status.calendarId || syncMutation.isPending}
                    >
                      Full
                    </Button>
                  </Stack>
                </Box>

                {/* Sync Errors */}
                {syncErrors && syncErrors.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningIcon color="warning" fontSize="small" />
                        <Typography variant="subtitle2">
                          {syncErrors.length} lesson(s) failed to sync
                        </Typography>
                      </Box>
                      <List dense sx={{ py: 0 }}>
                        {syncErrors.slice(0, 3).map((error) => (
                          <ListItem key={error.lessonId} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <ErrorIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={error.lessonName}
                              secondary={error.errorMessage}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </>
                )}

                <Divider />

                {/* Disconnect */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    startIcon={<LinkOffIcon />}
                    onClick={() => setShowDisconnectDialog(true)}
                  >
                    Disconnect
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Calendar Selection Dialog */}
      <Dialog
        open={showCalendarDialog}
        onClose={() => setShowCalendarDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Calendar</DialogTitle>
        <DialogContent>
          {calendarsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : calendars && calendars.length > 0 ? (
            <RadioGroup
              value={selectedCalendarId || ''}
              onChange={(e) => setSelectedCalendarId(e.target.value)}
            >
              {calendars.map((calendar: GoogleCalendar) => (
                <FormControlLabel
                  key={calendar.id}
                  value={calendar.id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {calendar.summary}
                        {calendar.primary && (
                          <Chip label="Primary" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      {calendar.description && (
                        <Typography variant="body2" color="text.secondary">
                          {calendar.description}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                />
              ))}
            </RadioGroup>
          ) : (
            <Alert severity="warning">
              No calendars found with write access. Please ensure you have at least one
              calendar in your Google account.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCalendarDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSelectCalendar}
            disabled={!selectedCalendarId || selectCalendarMutation.isPending}
          >
            {selectCalendarMutation.isPending ? 'Selecting...' : 'Select'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={handleCloseDisconnectDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disconnect Google Calendar?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will remove the connection to your Google Calendar. What should happen
            to the lessons already synced there?
          </Typography>
          <RadioGroup
            value={removeEventsOnDisconnect ? 'remove' : 'leave'}
            onChange={(e) => setRemoveEventsOnDisconnect(e.target.value === 'remove')}
          >
            <FormControlLabel
              value="remove"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">Remove my lessons from Google Calendar</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Synced lessons will be deleted from your Google Calendar.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="leave"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">Leave my lessons on Google Calendar</Typography>
                  <Typography variant="caption" color="text.secondary">
                    They&apos;ll stay on your calendar but won&apos;t update anymore.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDisconnectDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisconnect}
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
